import {
    SHARED_DASHBOARDS_KEY,
    getDashboardAccess,
    setDashboardAccess
} from '@/lib/dashboards-api';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { mutate as refreshCache } from 'swr';

import { DASHBOARD_LIST_KEY } from '@/components/Dashboards/useDashboardTitles';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { TableSkeleton } from '@/components/ui/table-skeleton';

import { DashboardAccessEditor } from './DashboardAccessEditor';

interface DashboardAccessDialogProps {
    dashboardId: string;
    title: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    showOrganizationNotice?: boolean;
}

export function DashboardAccessDialog({
    dashboardId,
    title,
    open,
    onOpenChange,
    showOrganizationNotice = false
}: DashboardAccessDialogProps) {
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [userIds, setUserIds] = useState<Set<string>>(new Set());
    const [groupIds, setGroupIds] = useState<Set<string>>(new Set());
    const [organizationAccess, setOrganizationAccess] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoaded(false);
        setLoadError(null);
        getDashboardAccess(dashboardId)
            .then((access) => {
                setUserIds(new Set(access.users.map((user) => user.userId)));
                setGroupIds(
                    new Set(access.groups.map((group) => group.groupId))
                );
                setOrganizationAccess(access.organization);
                setLoaded(true);
            })
            .catch((error) =>
                setLoadError(
                    error instanceof Error
                        ? error.message
                        : 'Could not load the access'
                )
            );
    }, [open, dashboardId]);

    const save = async () => {
        setSaving(true);
        try {
            await setDashboardAccess(dashboardId, {
                userIds: [...userIds],
                groupIds: [...groupIds],
                organization: organizationAccess
            });
            await refreshCache(DASHBOARD_LIST_KEY);
            await refreshCache(SHARED_DASHBOARDS_KEY);
            toast.success('Access saved');
            onOpenChange(false);
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? `Could not save: ${error.message}`
                    : 'Could not save'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="top-[8vh] flex flex-col max-w-3xl max-h-[84vh] translate-y-0">
                <DialogHeader>
                    <DialogTitle>Dashboard Access</DialogTitle>
                    <DialogDescription>
                        Choose who can view “{title}”. Everyone you give access
                        to can only view it.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 -mx-1 px-1 min-h-0 overflow-y-auto">
                    {loadError ? (
                        <p className="py-6 text-destructive text-sm">
                            {loadError}
                        </p>
                    ) : !loaded ? (
                        <TableSkeleton columns={3} />
                    ) : (
                        <DashboardAccessEditor
                            dashboardId={dashboardId}
                            active={open && loaded}
                            userIds={userIds}
                            groupIds={groupIds}
                            organizationAccess={organizationAccess}
                            onUserIdsChange={setUserIds}
                            onGroupIdsChange={setGroupIds}
                            onOrganizationAccessChange={setOrganizationAccess}
                            showOrganizationNotice={showOrganizationNotice}
                        />
                    )}
                </div>

                <DialogFooter className="pt-4 border-t">
                    <Button
                        variant="tertiary"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={!loaded || saving}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
